import { useCallback, useState } from "react";
import { useGetAccountPaymentPrefsQuery } from "../../../../graphql/supergraph.index";
import { clientContext } from "../../../../utils/graphql_utils";
import { usePositionListOfAccountWithoutPaymentPref } from "../../Components/PositionList/usePositionListOfAccountWithoutPaymentPref";

/**
 * A hook that manages payment preference state and data fetching for a specific account.
 * It handles fetching account payment preferences, positions without payment preferences,
 * and manages workflow states for creating and editing payment preferences.
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.accountId - ID of the account to manage payment preferences for
 *
 * @returns {Object} Hook return object
 * @returns {boolean} returns.dataLoading - Whether account or position data is being loaded
 * @returns {boolean} returns.dataError - Whether an error occurred during data fetching
 * @returns {Object} returns.accountPaymentPrefsData - Data containing account payment preferences
 * @returns {Object} returns.positionsPaginated - Paginated list of positions without payment preferences
 * @returns {Function} returns.handleLoadMorePositions - Function to load more positions
 * @returns {Function} returns.handleCreatePaymentPrefClick - Function to initiate payment pref creation
 * @returns {boolean} returns.showCreatePaymentPref - Whether to show the create workflow
 * @returns {string} returns.createPaymentPrefId - ID of payment pref being created
 * @returns {Function} returns.handleCancelCreatePaymentPref - Function to cancel creation workflow
 * @returns {Function} returns.handleSuccessCreatePaymentPref - Function called on successful creation
 * @returns {Function} returns.handleEditPaymentPrefClick - Function to initiate payment pref editing
 * @returns {boolean} returns.showEditPaymentPref - Whether to show the edit workflow
 * @returns {string} returns.editPaymentPrefId - ID of payment pref being edited
 * @returns {Function} returns.handleCancelEditPaymentPref - Function to cancel edit workflow
 * @returns {Function} returns.handleSuccessEditPaymentPref - Function called on successful edit
 * @returns {boolean} returns.showInstructionsNeededComponent - Whether to show instructions needed component
 * @returns {Function} returns.refetchData - Function to refetch all data
 * @returns {boolean} returns.canEdit - Whether user has edit permissions
 * @returns {boolean} returns.canReveal - Whether user has reveal permissions
 *
 * @example
 * ```tsx
 * const {
 *   dataLoading,
 *   accountPaymentPrefsData,
 *   handleCreatePaymentPrefClick,
 *   showCreatePaymentPref,
 *   canEdit,
 *   canReveal
 * } = useAccountPaymentPrefController({
 *   accountId: "123"
 * });
 * ```
 *
 * @remarks
 * - Uses Apollo Client queries for fetching payment preferences and positions
 * - Manages state for both create and edit workflows
 * - Provides loading and error states for UI feedback
 * - Handles permissions for editing and revealing payment preferences
 * - Supports pagination for positions without payment preferences
 */
export const useAccountPaymentPrefController = ({ accountId }: { accountId: string }) => {
  const {
    data: accountPaymentPrefsData,
    loading: accountPaymentPrefsLoading,
    error: accountPaymentPrefsError,
    refetch: refetchAccountPaymentPrefs,
  } = useGetAccountPaymentPrefsQuery({
    variables: {
      accountId,
      includeDeleted: true,
    },
    context: clientContext(),
  });

  const {
    positionsPaginated,
    handleLoadMorePositions,
    positionListLoading,
    positionListError,
    refetchPositionList,
  } = usePositionListOfAccountWithoutPaymentPref({ id: accountId });

  const [createPaymentPrefId, setCreatePaymentPrefId] = useState<string | undefined>(undefined);

  const refetchData = useCallback(async () => {
    await refetchAccountPaymentPrefs();
    await refetchPositionList();
  }, [refetchAccountPaymentPrefs, refetchPositionList]);

  const handleSuccessCreatePaymentPref = async () => {
    await refetchData();
    setCreatePaymentPrefId(undefined);
  };

  const [editPaymentPrefId, setEditPaymentPrefId] = useState<string | undefined>(undefined);

  const handleSuccessEditPaymentPref = async () => {
    await refetchData();
    setEditPaymentPrefId(undefined);
  };

  const canEdit =
    accountPaymentPrefsData?.currentUser?.canEditPaymentPrefsForAccountPermission ?? false;
  const canReveal =
    accountPaymentPrefsData?.currentUser?.canRevealPaymentPrefPermission ?? false;

  return {
    dataLoading: accountPaymentPrefsLoading || positionListLoading,
    dataError: accountPaymentPrefsError || positionListError,
    accountPaymentPrefsData,
    positionsPaginated,
    handleLoadMorePositions,
    handleCreatePaymentPrefClick: setCreatePaymentPrefId,
    showCreatePaymentPref: createPaymentPrefId !== undefined,
    createPaymentPrefId,
    handleCancelCreatePaymentPref: () => setCreatePaymentPrefId(undefined),
    handleSuccessCreatePaymentPref,
    handleEditPaymentPrefClick: setEditPaymentPrefId,
    showEditPaymentPref: editPaymentPrefId !== undefined,
    editPaymentPrefId,
    handleCancelEditPaymentPref: () => setEditPaymentPrefId(undefined),
    handleSuccessEditPaymentPref,
    showInstructionsNeededComponent: positionsPaginated?.count && positionsPaginated?.count > 0,
    refetchData,
    canEdit,
    canReveal,
  };
};
